import { useState, useEffect, useCallback } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { CsvController } from "../../common/parsers/CsvController";
import { fetchCompanyScope } from "../company_scope/companyScopeInteractionService";
import { fetchAttributeDefinitions } from "../../site-admin/declr_attribute_definition/declrAttributeDefinitionInteractionService";
import {
    fetchChartOfAccounts,
    addChartOfAccountsRow,
    updateChartOfAccountsRow,
    removeChartOfAccountsRow,
    bulkImportChartOfAccounts,
    setChartOfAccountsMapping,
    clearChartOfAccountsMapping,
    publishChartOfAccounts,
    fetchPublishedVersions,
} from "./companyChartOfAccountsInteractionService";

// Column-mapping targets offered when importing a file - mirrors the
// fields the bulk-import proc understands. SourceCode is the only
// mandatory one (the join key for create-vs-refresh on re-upload).
export const TARGET_FIELDS = [
    { key: "SourceCode", label: "GL / Source Code (required)" },
    { key: "Description", label: "Description" },
    { key: "XbrlTag", label: "XBRL Tag (if known)" },
    { key: "CategoryTag", label: "Category / Cost Type (reference only)" },
];

const normalize = (text) => (text || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const FIELD_SYNONYMS = {
    SourceCode: ["glcode", "sourcecode", "accountcode", "code", "costcode", "glaccount", "glaccountnum", "costcode2"],
    Description: ["description", "gldescription", "label", "name", "accountname"],
    XbrlTag: ["xbrltag", "xbrlconcept", "tag", "concept", "esrstag"],
    CategoryTag: ["category", "costtype", "esglinkage", "division", "budgetowner"],
};
const suggestFieldForHeader = (header) => {
    const n = normalize(header);
    for (const [field, synonyms] of Object.entries(FIELD_SYNONYMS)) {
        if (synonyms.includes(n)) return field;
    }
    return "";
};

const readFileAsBuffer = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error("Could not read file."));
        reader.readAsArrayBuffer(file);
    });

/**
 * 🎯 COMPANY CHART OF ACCOUNTS CONTROLLER
 * Manages the persistent dataset (Z_14): CRUD, CSV bulk import (client-side
 * parse only, no S3), manual scope mapping per row, and Publish (Z_15) -
 * the snapshot that's actually usable in a declaration.
 */
export default function DeclrChartOfAccountsController(companyId, siteKey) {
    const [rows, setRows] = useState([]);
    const [concepts, setConcepts] = useState([]);
    const [definitions, setDefinitions] = useState([]);
    const [publishedVersions, setPublishedVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Upload/mapping wizard state
    const [file, setFile] = useState(null);
    const [headers, setHeaders] = useState([]);
    const [sampleRows, setSampleRows] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [parsing, setParsing] = useState(false);
    const [importing, setImporting] = useState(false);

    const [publishing, setPublishing] = useState(false);

    const load = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);
        setError("");
        try {
            const [dataRows, scopeRows, defs, versions] = await Promise.all([
                fetchChartOfAccounts(companyId, { siteKey }),
                fetchCompanyScope(companyId, { siteKey }),
                fetchAttributeDefinitions(null, { siteKey }),
                fetchPublishedVersions(companyId, { siteKey }),
            ]);
            setRows(dataRows || []);
            // Only concepts the company already selected at
            // /registered/company-scope are offered here - the rep
            // shouldn't have to choose scope twice.
            setConcepts(scopeRows || []);
            setDefinitions(defs || []);
            setPublishedVersions(versions || []);
        } finally {
            setLoading(false);
        }
    }, [companyId, siteKey]);

    useEffect(() => {
        load();
    }, [load]);

    // ---- Manual row CRUD ----
    const handleAddRow = async (row) => {
        setError("");
        try {
            await addChartOfAccountsRow({ CorporateEntityId: companyId, ...row }, { siteKey });
            await load();
        } catch (err) {
            setError(err.message || "Could not add row.");
        }
    };

    const handleUpdateRow = async (row) => {
        setError("");
        try {
            await updateChartOfAccountsRow(row, { siteKey });
            await load();
        } catch (err) {
            setError(err.message || "Could not update row.");
        }
    };

    const handleRemoveRow = async (chartOfAccountsRowId) => {
        setError("");
        try {
            await removeChartOfAccountsRow(chartOfAccountsRowId, { siteKey });
            await load();
        } catch (err) {
            setError(err.message || "Could not remove row.");
        }
    };

    // ---- Mapping ----
    const handleSetMapping = async (mapping) => {
        setError("");
        try {
            await setChartOfAccountsMapping(mapping, { siteKey });
            await load();
        } catch (err) {
            setError(err.message || "Could not save mapping.");
        }
    };

    const handleClearMapping = async (chartOfAccountsRowId) => {
        setError("");
        try {
            await clearChartOfAccountsMapping(chartOfAccountsRowId, { siteKey });
            await load();
        } catch (err) {
            setError(err.message || "Could not clear mapping.");
        }
    };

    // ---- CSV upload wizard ----
    const handleFileSelect = async (selectedFile) => {
        if (!selectedFile) return;
        setError("");
        setFile(selectedFile);
        setParsing(true);
        try {
            const buffer = await readFileAsBuffer(selectedFile);
            const parsed = await CsvController.execute(buffer, () => {});
            const sheet = parsed.worksheets[0];
            setHeaders(sheet.headers);
            setSampleRows(sheet.sampleRows);

            const initialMapping = {};
            sheet.headers.forEach((h, i) => {
                const suggestion = suggestFieldForHeader(h);
                if (suggestion) initialMapping[i] = suggestion;
            });
            setColumnMapping(initialMapping);
        } catch (err) {
            setError(err.message || "Could not parse file.");
            setFile(null);
        } finally {
            setParsing(false);
        }
    };

    const handleMappingChange = (headerIndex, targetField) => {
        setColumnMapping((prev) => ({ ...prev, [headerIndex]: targetField }));
    };

    const isSourceCodeMapped = Object.values(columnMapping).includes("SourceCode");

    const handleImport = async () => {
        if (!file || !isSourceCodeMapped) return;
        setImporting(true);
        setError("");
        try {
            const buffer = await readFileAsBuffer(file);
            const parsed = await CsvController.executeFull(buffer, () => {});
            const dataRows = parsed.worksheets[0].dataRows;

            const importRows = dataRows
                .map((rawRow) => {
                    const row = {};
                    Object.entries(columnMapping).forEach(([headerIndex, targetField]) => {
                        if (!targetField) return;
                        row[targetField] = rawRow[Number(headerIndex)];
                    });
                    return row;
                })
                .filter((row) => row.SourceCode && String(row.SourceCode).trim() !== "");

            await bulkImportChartOfAccounts(companyId, importRows, { siteKey });
            setFile(null);
            setHeaders([]);
            setSampleRows([]);
            setColumnMapping({});
            await load();
        } catch (err) {
            setError(err.message || "Bulk import failed.");
        } finally {
            setImporting(false);
        }
    };

    const cancelUpload = () => {
        setFile(null);
        setHeaders([]);
        setSampleRows([]);
        setColumnMapping({});
    };

    // ---- Publish ----
    const handlePublish = async () => {
        setPublishing(true);
        setError("");
        try {
            await publishChartOfAccounts(companyId, { siteKey });
            const versions = await fetchPublishedVersions(companyId, { siteKey });
            setPublishedVersions(versions || []);
        } catch (err) {
            setError(err.message || "Publish failed.");
        } finally {
            setPublishing(false);
        }
    };

    const mappedCount = rows.filter((r) => GoldenSourceHelper.get(r, "MappingStatusCode") === "MAPPED").length;

    return {
        rows,
        concepts,
        definitions,
        publishedVersions,
        loading,
        error,
        mappedCount,

        file,
        headers,
        sampleRows,
        columnMapping,
        parsing,
        importing,
        isSourceCodeMapped,
        handleFileSelect,
        handleMappingChange,
        handleImport,
        cancelUpload,

        handleAddRow,
        handleUpdateRow,
        handleRemoveRow,
        handleSetMapping,
        handleClearMapping,

        publishing,
        handlePublish,
    };
}
